import {
  type BriefingCreateDefinitionPersistState,
  imageAssetsToState,
  mapBriefingCreateStateToCreateInput,
  mapBriefingCreateStateToUpdateInput,
  mapBriefingDefinitionToBriefingCreateHydration,
} from "@/lib/scheduled-briefings/repository/briefingDefinitionMappers";
import type {
  BriefingDefinitionRepository,
  BriefingDefinitionRepositoryContext,
  BriefingDefinitionRepositoryError,
} from "@/lib/scheduled-briefings/repository/briefingDefinitionRepository.types";
import { graphqlBriefingDefinitionRepository } from "@/lib/scheduled-briefings/repository/graphqlBriefingDefinitionRepository";
import useAuth from "@/store/auth.slice";
import type { BriefingDefinition } from "@/types/briefing-definition/briefingDefinition.types";
import useBriefingCreateStore, {
  type BriefingCreateState,
  pickBriefingCreateForm,
  selectRecipientsError,
} from "./briefing-create.slice";

export interface BriefingCreateDefinitionAsyncDeps {
  repository?: BriefingDefinitionRepository;
  context?: BriefingDefinitionRepositoryContext;
}

export interface SaveBriefingDefinitionOptions
  extends BriefingCreateDefinitionAsyncDeps {
  /** Template slot ids in layout order. */
  slotIds: readonly string[];
  /** Template `updatedAt` for create; falls back to store `templateUpdatedAt` when set. */
  templateUpdatedAt?: string;
  /** Saved when `briefingName` is unset or blank (template title or "New Briefing"). */
  fallbackBriefingName?: string;
}

const unexpectedRequestFailure: BriefingDefinitionRepositoryError = {
  code: "REQUEST_FAILURE",
  message: "Briefing definition request failed.",
};

const resolveRepository = (
  repository?: BriefingDefinitionRepository,
): BriefingDefinitionRepository =>
  repository ?? graphqlBriefingDefinitionRepository;

const resolveRepositoryContext = (
  context?: BriefingDefinitionRepositoryContext,
): BriefingDefinitionRepositoryContext => {
  if (context !== undefined) return context;

  const { user } = useAuth.getState();
  return {
    tenantId: String(user.default_tenant.id),
    userId: String(user.id),
  };
};

const pickPersistState = (
  state: BriefingCreateState,
): BriefingCreateDefinitionPersistState => ({
  selectedTemplateId: state.selectedTemplateId,
  briefingName: state.briefingName,
  overallPrompt: state.overallPrompt,
  lookbackDays: state.lookbackDays,
  slotInputs: state.slotInputs,
  slotTitles: state.slotTitles,
  imageAssets: state.imageAssets,
  scheduleFrequency: state.scheduleFrequency,
  scheduleTime: state.scheduleTime,
  timezone: state.timezone,
  validRecipientEmails: state.validRecipientEmails,
});

const applyDefinitionFailure = (
  error: BriefingDefinitionRepositoryError,
  mode: "load" | "save",
) => {
  useBriefingCreateStore.setState(
    {
      definitionError: error,
      ...(mode === "load"
        ? { isFetchingBriefingDefinition: false }
        : { isSavingBriefingDefinition: false }),
    },
    false,
    mode === "load"
      ? "[System] Briefing create: Load definition failed"
      : "[System] Briefing create: Save definition failed",
  );
};

const applyDefinitionVersions = (definition: BriefingDefinition) => {
  useBriefingCreateStore.setState(
    {
      definitionId: definition.id,
      definitionUpdatedAt: definition.updatedAt,
      templateUpdatedAt: definition.templateUpdatedAt,
    },
    false,
    "[System] Briefing create: Persist definition version",
  );
};

/**
 * Loads a briefing definition for edit and hydrates the create/edit store.
 */
export const loadDefinition = async (
  briefingId: string,
  deps: BriefingCreateDefinitionAsyncDeps = {},
): Promise<boolean> => {
  // Reset clears the previous briefing's id and form and bumps the request id,
  // so an in-flight load or save cannot write into this briefing.
  useBriefingCreateStore.getState().resetBriefingCreate();
  const requestId = useBriefingCreateStore.getState().definitionRequestId;

  useBriefingCreateStore.setState(
    { isFetchingBriefingDefinition: true },
    false,
    "[User] Briefing create: Load definition",
  );

  let result: Awaited<ReturnType<BriefingDefinitionRepository["get"]>>;
  try {
    const repository = resolveRepository(deps.repository);
    const context = resolveRepositoryContext(deps.context);
    result = await repository.get(context, briefingId);
  } catch {
    result = { success: false, error: unexpectedRequestFailure };
  }

  if (useBriefingCreateStore.getState().definitionRequestId !== requestId) {
    return false;
  }

  if (!result.success) {
    applyDefinitionFailure(result.error, "load");
    return false;
  }

  const hydration = mapBriefingDefinitionToBriefingCreateHydration(result.data);
  if (!hydration.success) {
    applyDefinitionFailure(hydration.error, "load");
    return false;
  }

  const {
    definitionId,
    definitionUpdatedAt,
    templateUpdatedAt,
    canEdit,
    form,
  } = hydration.data;

  useBriefingCreateStore.setState(
    {
      definitionId,
      definitionUpdatedAt,
      templateUpdatedAt,
      canEditDefinition: canEdit,
      ...form,
      savedForm: pickBriefingCreateForm(form),
      isFetchingBriefingDefinition: false,
      definitionError: null,
    },
    false,
    "[System] Briefing create: Load definition success",
  );

  return true;
};

/**
 * Creates or updates a briefing definition from the current store state.
 * Resolves to the saved definition id, or `null` when the save did not apply.
 */
export const saveDefinition = async (
  options: SaveBriefingDefinitionOptions,
): Promise<string | null> => {
  const {
    definitionRequestId,
    isFetchingBriefingDefinition,
    isSavingBriefingDefinition,
  } = useBriefingCreateStore.getState();
  // A second create would duplicate the definition; a second update would self-conflict.
  if (isFetchingBriefingDefinition || isSavingBriefingDefinition) {
    return null;
  }

  // Save a typed recipient that hasn't been added as a chip yet.
  useBriefingCreateStore.getState().commitRecipientInput();
  // The Recipient emails field shows the error instead.
  if (selectRecipientsError(useBriefingCreateStore.getState())) return null;

  const requestId = definitionRequestId + 1;

  useBriefingCreateStore.setState(
    {
      definitionRequestId: requestId,
      isSavingBriefingDefinition: true,
      definitionError: null,
    },
    false,
    "[User] Briefing create: Save definition",
  );

  const state = useBriefingCreateStore.getState();
  const {
    slotIds,
    templateUpdatedAt: templateUpdatedAtOption,
    fallbackBriefingName,
  } = options;
  const persistState = {
    ...pickPersistState(state),
    briefingName: state.briefingName?.trim()
      ? state.briefingName
      : (fallbackBriefingName ?? null),
  };

  let result: Awaited<ReturnType<BriefingDefinitionRepository["create"]>>;

  try {
    const repository = resolveRepository(options.repository);
    const context = resolveRepositoryContext(options.context);

    if (state.definitionId === null) {
      const templateUpdatedAt =
        templateUpdatedAtOption ?? state.templateUpdatedAt;
      if (templateUpdatedAt === null) {
        applyDefinitionFailure(
          {
            code: "INVALID_CONFIGURATION",
            message: "Template version is required to create a briefing.",
            validationErrors: [
              {
                path: "templateUpdatedAt",
                message: "Choose a template again and retry.",
              },
            ],
          },
          "save",
        );
        return null;
      }

      const mappedInput = mapBriefingCreateStateToCreateInput(persistState, {
        slotIds,
        templateUpdatedAt,
      });
      if (!mappedInput.success) {
        applyDefinitionFailure(mappedInput.error, "save");
        return null;
      }

      result = await repository.create(context, mappedInput.data);
    } else {
      const { definitionId, definitionUpdatedAt, templateUpdatedAt } = state;
      if (definitionUpdatedAt === null) {
        applyDefinitionFailure(
          {
            code: "REQUEST_FAILURE",
            message: "Briefing version is missing; reload and try again.",
          },
          "save",
        );
        return null;
      }

      const mappedInput = mapBriefingCreateStateToUpdateInput(persistState, {
        slotIds,
        definitionId,
        expectedUpdatedAt: definitionUpdatedAt,
        templateUpdatedAt,
      });
      if (!mappedInput.success) {
        applyDefinitionFailure(mappedInput.error, "save");
        return null;
      }

      result = await repository.update(context, mappedInput.data);
    }
  } catch {
    result = { success: false, error: unexpectedRequestFailure };
  }

  if (useBriefingCreateStore.getState().definitionRequestId !== requestId) {
    return null;
  }

  if (!result.success) {
    applyDefinitionFailure(result.error, "save");
    return null;
  }

  const imageAssets = imageAssetsToState(result.data.imageAssets);
  useBriefingCreateStore.setState(
    (state) => ({
      isSavingBriefingDefinition: false,
      definitionError: null,
      // Keep image edits made while the save was in flight.
      ...(state.imageAssets === persistState.imageAssets
        ? { imageAssets }
        : {}),
      savedForm: pickBriefingCreateForm({ ...state, imageAssets }),
    }),
    false,
    "[System] Briefing create: Save definition success",
  );
  applyDefinitionVersions(result.data);

  return result.data.id;
};
